(function () {
  const ALLOWED_UPLOAD_PATTERN = /\.(ai|bmp|jpe?g|pdf|png|svg|webp)$/i;
  const PREVIEWABLE_PATTERN = /\.(png|jpe?g|svg|webp|gif|bmp)$/i;
  const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
  const ROTATION_SNAP = 15;
  const ARTWORK_DEFAULT_SCALE = 50;
  const TRASH_ICON = `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M9.25 4h5.5l.65 1.35H19.5v1.7h-15v-1.7h4.1L9.25 4Z" fill="currentColor"></path>
      <path d="M7.1 8.1h9.8l-.75 10.2a1.75 1.75 0 0 1-1.75 1.6H9.6a1.75 1.75 0 0 1-1.75-1.6L7.1 8.1Z" fill="currentColor"></path>
      <path d="M10.2 10.4v6.1M13.8 10.4v6.1" fill="none" stroke="rgba(255,255,255,0.96)" stroke-linecap="round" stroke-width="1.4"></path>
    </svg>
  `;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const uid = () => `lbec-${Math.random().toString(36).slice(2, 10)}`;
  const snapRotation = (value) => Math.round(value / ROTATION_SNAP) * ROTATION_SNAP;
  const FONT_CHOICES = [
    'Arial', 'Arial Black', 'Aptos', 'Avenir', 'Baskerville', 'Bodoni 72', 'Book Antiqua', 'Bookman Old Style', 'Bradley Hand', 'Brush Script MT',
    'Calibri', 'Cambria', 'Candara', 'Century Gothic', 'Chalkboard SE', 'Charter', 'Cochin', 'Comic Sans MS', 'Consolas', 'Constantia',
    'Copperplate', 'Courier New', 'Didot', 'DIN Alternate', 'DIN Condensed', 'Ebrima', 'Futura', 'Garamond', 'Geneva', 'Georgia',
    'Gill Sans', 'Helvetica', 'Helvetica Neue', 'Hoefler Text', 'Impact', 'Iowan Old Style', 'Lucida Bright', 'Lucida Calligraphy', 'Lucida Console', 'Lucida Grande',
    'Lucida Handwriting', 'Lucida Sans', 'Menlo', 'Monaco', 'Montserrat', 'MS Gothic', 'Myanmar Text', 'Noteworthy', 'Optima', 'Palatino',
    'Papyrus', 'Perpetua', 'Rockwell', 'Segoe Print', 'Segoe Script', 'Segoe UI', 'Skia', 'Snell Roundhand', 'Tahoma', 'Times New Roman',
    'Trebuchet MS', 'Verdana', 'American Typewriter', 'Andale Mono', 'Apple Chancery', 'Apple SD Gothic Neo', 'Arial Narrow', 'Athelas', 'Ayuthaya', 'Big Caslon',
    'Bodoni 72 Smallcaps', 'Bradley Hand ITC', 'Brush Script Std', 'Chalkduster', 'Corsiva Hebrew', 'DecoType Naskh', 'Devanagari Sangam MN', 'Euphemia UCAS', 'Farah', 'Franklin Gothic Medium',
    'Geeza Pro', 'Gujarati Sangam MN', 'Gurmukhi MN', 'Herculanum', 'Hiragino Maru Gothic ProN', 'Kannada Sangam MN', 'Kefa', 'Khmer Sangam MN', 'Kohinoor Bangla', 'Kohinoor Devanagari',
    'Kohinoor Telugu', 'Krungthep', 'Lao Sangam MN', 'Malayalam Sangam MN', 'Marker Felt', 'Mishafi', 'Oriya Sangam MN', 'Party LET', 'Phosphate', 'Plantagenet Cherokee',
    'Savoye LET', 'Seravek', 'SignPainter', 'Silom', 'Sinhala Sangam MN', 'STIX Two Text', 'Tamil Sangam MN', 'Telugu Sangam MN', 'Thonburi', 'Trattatello'
  ];

  const resolveFontMeta = (select) => {
    const option = select?.selectedOptions?.[0];
    return {
      label: option?.value || 'Classic serif',
      stack: option?.dataset?.fontStack || 'Georgia, "Times New Roman", serif',
      weight: option?.dataset?.fontWeight || '700',
    };
  };

  const resolvePlacementMeta = (select) => {
    const option = select?.selectedOptions?.[0];
    return {
      label: option?.value || 'Centered',
      x: Number(option?.dataset?.placementX ?? 50),
      y: Number(option?.dataset?.placementY ?? 50),
    };
  };

  const initCustomizer = (root) => {
    if (!root || root.dataset.lbecReady === 'true') return;
    root.dataset.lbecReady = 'true';

    const zone = root.querySelector('[data-lbec-zone]');
    const stage = root.querySelector('[data-lbec-stage]');
    const productTitle =
      root.closest('.lbec-live-customizer-section')?.querySelector('.lbec-live-customizer-section__product-title')?.textContent?.trim() ||
      root.closest('.lbec-live-customizer-section')?.querySelector('.lbec-live-customizer-section__title')?.textContent?.trim() ||
      '';
    const maxArtworks = Number(root.dataset.maxArtworks || 5);
    const productUrl = root.dataset.productUrl || '';
    const backUrl = root.dataset.backUrl || '/collections/customizable-products';
    const defaultPriceCents = Number(root.dataset.defaultPriceCents || 0);
    const defaultAvailable = root.dataset.defaultAvailable !== 'false';
    const allowVariantMedia = root.dataset.allowVariantMedia === 'true';
    const currencyCode = root.dataset.currencyCode || 'USD';
    const moneyLocale = root.dataset.moneyLocale || document.documentElement.lang || 'en-US';
    const normalizedBackPath = backUrl.startsWith('shopify://collections/')
      ? `/collections/${backUrl.replace('shopify://collections/', '')}`
      : backUrl;
    const fallbackBackUrl = normalizedBackPath.startsWith('http')
      ? normalizedBackPath
      : `${window.location.origin}${normalizedBackPath.startsWith('/') ? normalizedBackPath : `/${normalizedBackPath}`}`;

    const dataInput = root.querySelector('[data-lbec-data-input]');
    const productForm = root.querySelector('form');
    const textSummaryInput = root.querySelector('[data-lbec-text-summary-input]');
    const artworkSummaryInput = root.querySelector('[data-lbec-artwork-summary-input]');
    const previewSnapshotInput = root.querySelector('[data-lbec-preview-snapshot-input]');
    const orderTypeInput = root.querySelector('[data-lbec-order-type-input]');
    const workflowInput = root.querySelector('[data-lbec-workflow-input]');
    const stageImage = root.querySelector('[data-lbec-stage-image]');
    const galleryButtons = Array.from(root.querySelectorAll('[data-lbec-gallery-image]'));

    const variantSelect = root.querySelector('[data-lbec-variant-select]');
    const variantButtons = Array.from(root.querySelectorAll('[data-lbec-variant-button]'));
    const modeButtons = Array.from(root.querySelectorAll('[data-lbec-mode-button]'));
    const workflowButtons = Array.from(root.querySelectorAll('[data-lbec-workflow-button]'));
    const workflowPanels = Array.from(root.querySelectorAll('[data-lbec-workflow-panel]'));
    const customizationCards = Array.from(root.querySelectorAll('[data-lbec-customization-card]'));
    const variantHidden = root.querySelector('[data-lbec-variant-hidden]');
    const unitPriceNode = root.querySelector('[data-lbec-unit-price]');
    const estimatedTotalNode = root.querySelector('[data-lbec-estimated-total]');
    const variantImageMapNode = root.querySelector('[data-lbec-variant-image-map]');
    const quantityInput = root.querySelector('[data-lbec-qty-input]');
    const qtyDown = root.querySelector('[data-lbec-qty-down]');
    const qtyUp = root.querySelector('[data-lbec-qty-up]');
    const submitButton = root.querySelector('[data-lbec-submit-button]');
    const shareButton = root.querySelector('[data-lbec-share-button]');
    const shareStatus = root.querySelector('[data-lbec-share-status]');
    const backButton = root.querySelector('[data-lbec-back-button]');
    const statusNode = root.querySelector('[data-lbec-status]');

    let variantImageMap = [];
    if (variantImageMapNode?.textContent?.trim()) {
      try {
        variantImageMap = JSON.parse(variantImageMapNode.textContent);
      } catch (error) {
        variantImageMap = [];
      }
    }

    const formatMoney = (cents) => {
      const amount = Number(cents || 0) / 100;
      try {
        return new Intl.NumberFormat(moneyLocale, {
          style: 'currency',
          currency: currencyCode,
          maximumFractionDigits: 2,
        }).format(amount);
      } catch (error) {
        return `$${amount.toFixed(2)}`;
      }
    };

    const uploadButtons = Array.from(root.querySelectorAll('[data-lbec-trigger-upload-inline]'));
    const libraryButtons = Array.from(root.querySelectorAll('[data-lbec-open-library-inline]'));
    const artworkInput = root.querySelector('[data-lbec-artwork-input]');

    const textLayer = root.querySelector('[data-lbec-text-layer]');
    const textInput = root.querySelector('[data-lbec-text-input]');
    const textClearButton = root.querySelector('[data-lbec-text-clear]');
    const textAddButton = root.querySelector('[data-lbec-text-add]');
    const textActionButtons = Array.from(root.querySelectorAll('[data-lbec-text-action]'));
    const textFormatButtons = Array.from(root.querySelectorAll('[data-lbec-text-format]'));
    const fontSelect = root.querySelector('[data-lbec-font-select]');
    const textPlacementSelect = root.querySelector('[data-lbec-placement-select]');
    const textSizeInput = root.querySelector('[data-lbec-text-size]');
    const textRotationInput = root.querySelector('[data-lbec-text-rotation]');

    const artworkCanvas = root.querySelector('[data-lbec-artwork-canvas]');
    const artworkList = root.querySelector('[data-lbec-artwork-list]');
    const artworkControls = root.querySelector('[data-lbec-artwork-controls]');
    const selectedArtworkLabel = root.querySelector('[data-lbec-selected-artwork-label]');
    const artworkActionButtons = Array.from(root.querySelectorAll('[data-lbec-artwork-action]'));
    const alignButtons = Array.from(root.querySelectorAll('[data-lbec-align-target]'));
    const artPlacementSelect = root.querySelector('[data-lbec-art-placement-select]');
    const artScaleInput = root.querySelector('[data-lbec-artwork-scale]');
    const artRotationInput = root.querySelector('[data-lbec-artwork-rotation]');

    const library = root.querySelector('[data-lbec-library]');
    const libraryCategory = root.querySelector('[data-lbec-library-category]');
    const librarySubcategoryWrap = root.querySelector('[data-lbec-library-subcategory-wrap]');
    const librarySubcategory = root.querySelector('[data-lbec-library-subcategory]');
    const librarySearch = root.querySelector('[data-lbec-library-search]');
    const libraryGrid = root.querySelector('[data-lbec-library-grid]');

    const selectionBox = root.querySelector('[data-lbec-selection-box]');
    const zoneShape = (root.dataset.zoneShape || 'rectangle').trim();
    const zoneEnabled = root.dataset.zoneEnabled === 'true';
    const initialStageSrc = stageImage?.getAttribute('src') || '';
    const initialStageAlt = stageImage?.getAttribute('alt') || productTitle || 'Product preview';
    const customizerImageSrc = root.dataset.customizerImageSrc || initialStageSrc;
    const customizerImageAlt = root.dataset.customizerImageAlt || productTitle || initialStageAlt;

    if (zoneShape === 'circle' && zoneEnabled) {
      zone?.classList.add('is-circle');
    } else {
      zone?.classList.remove('is-circle');
    }
    zone?.classList.toggle('is-unbounded', !zoneEnabled);

    if (fontSelect && !fontSelect.options.length) {
      FONT_CHOICES.forEach((fontName, index) => {
        const option = document.createElement('option');
        option.value = fontName;
        option.textContent = fontName;
        option.dataset.fontStack = `"${fontName}", ${index < 70 ? 'Arial, sans-serif' : 'Georgia, serif'}`;
        option.dataset.fontWeight = '700';
        fontSelect.appendChild(option);
      });
      fontSelect.value = 'Georgia';
    }

    const state = {
      activeLayer: null,
      interaction: null,
      mode: 'custom',
      workflow: 'upload',
      pendingUploads: 0,
      submitted: false,
      preview: {
        customizing: false,
        activeSrc: initialStageSrc,
        activeAlt: initialStageAlt,
        hoverSrc: '',
        hoverAlt: '',
        customizerSrc: customizerImageSrc,
        customizerAlt: customizerImageAlt,
      },
      text: {
        enabled: false,
        content: '',
        draft: '',
        bold: true,
        italic: false,
        underline: false,
        x: 50,
        y: 50,
        size: Number(textSizeInput?.value || 32),
        rotation: Number(textRotationInput?.value || 0),
        font: resolveFontMeta(fontSelect),
        placementLabel: resolvePlacementMeta(textPlacementSelect).label,
      },
      artworks: [],
      library: {
        open: false,
        loaded: false,
        items: [],
        category: 'all',
        subcategory: 'all',
        search: '',
      },
    };

    const formatBytes = (bytes) => {
      if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
      if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
      return `${bytes} bytes`;
    };

    const setStatus = (message, type = 'error') => {
      if (!statusNode) return;
      statusNode.textContent = message;
      statusNode.hidden = false;
      statusNode.classList.toggle('is-note', type === 'note');
    };

    const clearStatus = () => {
      if (!statusNode) return;
      statusNode.textContent = '';
      statusNode.hidden = true;
      statusNode.classList.remove('is-note');
    };

    const focusStatus = () => {
      if (!statusNode || statusNode.hidden) return;
      statusNode.focus({ preventScroll: true });
      statusNode.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const getArtworkById = (id) => state.artworks.find((item) => item.id === id) || null;

    const getActiveArtwork = () => {
      if (!state.activeLayer || state.activeLayer.type !== 'artwork') return null;
      return getArtworkById(state.activeLayer.id);
    };

    const getActiveTextArtwork = () => {
      const activeArtwork = getActiveArtwork();
      return activeArtwork?.kind === 'text' ? activeArtwork : null;
    };

    const getArtworkItemsInOrder = () =>
      [...state.artworks].sort((a, b) => a.order - b.order);

    const getTextItemsInOrder = () =>
      getArtworkItemsInOrder().filter((item) => item.kind === 'text');

    const getUploadedArtworkItems = () =>
      getArtworkItemsInOrder().filter((item) => item.kind !== 'text');

    const syncArtworkUploadInput = () => {
      if (!artworkInput || typeof DataTransfer === 'undefined') return;
      try {
        const transfer = new DataTransfer();
        getArtworkItemsInOrder().forEach((item) => {
          if (item.file) {
            transfer.items.add(item.file);
          }
        });
        artworkInput.files = transfer.files;
      } catch (error) {
        setStatus('The artwork preview is saved, but this browser could not keep the original upload attached. Please upload the artwork again before adding to cart.');
      }
    };

    const setActiveLayer = (layer) => {
      state.activeLayer = layer;
      render();
    };

    const clearActiveLayer = () => {
      state.activeLayer = null;
      render();
    };

    const getQuantity = () => Math.max(1, Number(quantityInput?.value || 1));

    const getSelectedVariantPriceCents = () => {
      const option = variantSelect?.selectedOptions?.[0];
      return Number(option?.dataset?.priceCents || defaultPriceCents || 0);
    };

    const getSelectedVariantAvailable = () => {
      const option = variantSelect?.selectedOptions?.[0];
      return option ? option.dataset.available !== 'false' : defaultAvailable;
    };

    const getSelectedVariantIsBlank = () => {
      const option = variantSelect?.selectedOptions?.[0];
      return option?.dataset.blankVariant === 'true';
    };

    const syncDisplayedPrice = () => {
      const unitPrice = getSelectedVariantPriceCents();
      if (unitPriceNode) {
        unitPriceNode.textContent = formatMoney(unitPrice);
      }
      if (estimatedTotalNode) {
        estimatedTotalNode.textContent = formatMoney(unitPrice * getQuantity());
      }
    };

    const syncSubmitAvailability = () => {
      if (!submitButton) return;
      const available = getSelectedVariantAvailable();
      const waitingForUploads = state.pendingUploads > 0;
      submitButton.disabled = !available || waitingForUploads;
      const availableLabel = state.mode === 'blank'
        ? submitButton.dataset.blankLabel || 'Add blanks to cart'
        : submitButton.dataset.customLabel || submitButton.dataset.availableLabel || 'Add customized item to cart';
      if (waitingForUploads) {
        submitButton.textContent = 'Preparing artwork...';
        return;
      }
      submitButton.textContent = available ? availableLabel : submitButton.dataset.unavailableLabel || 'Product unavailable';
    };

    const syncUploadButtons = () => {
      const hasUploadedArtwork = getUploadedArtworkItems().length > 0;
      uploadButtons.forEach((button) => {
        button.textContent = hasUploadedArtwork ? 'Upload different artwork' : 'Upload artwork';
      });
    };

    const getVariantCustomizerImage = (option) => {
      if (!option) {
        return {
          src: state.preview.customizerSrc,
          alt: state.preview.customizerAlt,
        };
      }

      const variantTitle = (option.dataset.variantTitle || option.textContent || '').trim();
      const override = variantImageMap.find((item) => item.matchLabel === variantTitle);
      const src = override?.imageSrc || (allowVariantMedia ? option.dataset.imageSrc : '') || state.preview.customizerSrc;
      const alt = override?.imageAlt || option.dataset.imageAlt || state.preview.customizerAlt || productTitle || 'Product preview';
      return { src, alt };
    };

    const setStageImage = (src, alt) => {
      if (!stageImage || !src) return;
      if (stageImage.getAttribute('src') !== src) {
        stageImage.src = src;
      }
      stageImage.alt = alt || productTitle || 'Product preview';
    };

    const syncGalleryActive = () => {
      galleryButtons.forEach((button) => {
        button.classList.toggle('is-active', button.dataset.imageSrc === state.preview.activeSrc);
      });
    };

    const applyPreviewImage = () => {
      const src = state.preview.hoverSrc || state.preview.activeSrc || state.preview.customizerSrc;
      const alt = state.preview.hoverAlt || state.preview.activeAlt || state.preview.customizerAlt;
      setStageImage(src, alt);
      syncGalleryActive();
    };

    const switchToCustomizerImage = () => {
      state.preview.customizing = true;
      const variantImage = getVariantCustomizerImage(variantSelect?.selectedOptions?.[0]);
      state.preview.activeSrc = variantImage.src;
      state.preview.activeAlt = variantImage.alt;
      applyPreviewImage();
    };

    const syncVariant = () => {
      const option = variantSelect?.selectedOptions?.[0];
      if (variantHidden && option?.value) {
        variantHidden.value = option.value;
      }
      syncDisplayedPrice();
      syncSubmitAvailability();
      variantButtons.forEach((button) => {
        button.classList.toggle('is-active', button.dataset.lbecVariantButton === option?.value);
      });
      if (state.preview.customizing) {
        const variantImage = getVariantCustomizerImage(option);
        state.preview.activeSrc = variantImage.src;
        state.preview.activeAlt = variantImage.alt;
      }
      applyPreviewImage();
    };

    const closeMenus = () => {
      root.querySelectorAll('.lbec-live-customizer__art-menu[open]').forEach((menu) => {
        menu.removeAttribute('open');
      });
    };

    const hideLibrary = () => {
      state.library.open = false;
      if (library) {
        library.hidden = true;
      }
    };

    const getWorkflowLabel = () => state.workflow === 'designs' ? 'Use our designs' : 'Upload your artwork';

    const setWorkflow = (workflow) => {
      state.workflow = workflow === 'designs' ? 'designs' : 'upload';
      if (state.workflow === 'upload') {
        hideLibrary();
      }
      render();
    };

    const openLibrary = async () => {
      if (!library || !libraryGrid) return;
      state.library.open = true;
      library.hidden = false;
      closeMenus();
      if (state.library.loaded) {
        renderLibrary();
        return;
      }

      const libraryUrl = root.dataset.designLibraryUrl;
      if (!libraryUrl) return;

      try {
        const response = await fetch(libraryUrl, { credentials: 'same-origin' });
        const payload = await response.json();
        const items = Array.isArray(payload?.categories)
          ? payload.categories.flatMap((category) => {
              const rootItems = (category.items || []).map((item) => ({
                id: item.id || uid(),
                name: item.name || 'Untitled design',
                category: category.name || item.category || 'Other',
                subcategory: item.subcategory || '',
                previewSrc: item.preview_src || '',
              }));
              const nestedItems = (category.subcategories || []).flatMap((subcategory) =>
                (subcategory.items || []).map((item) => ({
                  id: item.id || uid(),
                  name: item.name || 'Untitled design',
                  category: category.name || item.category || 'Other',
                  subcategory: subcategory.name || item.subcategory || '',
                  previewSrc: item.preview_src || '',
                }))
              );
              return [...rootItems, ...nestedItems];
            })
          : [];

        state.library.items = items;
        state.library.loaded = true;
        populateLibraryCategories();
        populateLibrarySubcategories();
        renderLibrary();
      } catch (error) {
        libraryGrid.innerHTML = '<p class="lbec-live-customizer__empty-note">Built-in artwork is unavailable right now. Upload artwork to continue.</p>';
      }
    };

    const populateLibraryCategories = () => {
      if (!libraryCategory) return;
      const categories = Array.from(new Set(state.library.items.map((item) => item.category).filter(Boolean))).sort();
      libraryCategory.innerHTML = '<option value="all">All categories</option>';
      categories.forEach((category) => {
        const option = document.createElement('option');
        option.value = category;
        option.textContent = category;
        libraryCategory.appendChild(option);
      });
      libraryCategory.value = state.library.category;
    };

    const populateLibrarySubcategories = () => {
      if (!librarySubcategory || !librarySubcategoryWrap) return;

      const subcategories =
        state.library.category === 'all'
          ? []
          : Array.from(
              new Set(
                state.library.items
                  .filter((item) => item.category === state.library.category && item.subcategory)
                  .map((item) => item.subcategory)
              )
            ).sort();

      if (!subcategories.length) {
        state.library.subcategory = 'all';
        librarySubcategoryWrap.hidden = true;
        librarySubcategory.innerHTML = '<option value="all">All subcategories</option>';
        librarySubcategory.value = 'all';
        return;
      }

      librarySubcategoryWrap.hidden = false;
      librarySubcategory.innerHTML = '<option value="all">All subcategories</option>';
      subcategories.forEach((subcategory) => {
        const option = document.createElement('option');
        option.value = subcategory;
        option.textContent = subcategory;
        librarySubcategory.appendChild(option);
      });

      const nextValue = subcategories.includes(state.library.subcategory)
        ? state.library.subcategory
        : 'all';
      state.library.subcategory = nextValue;
      librarySubcategory.value = nextValue;
    };

    const filteredLibraryItems = () => {
      const query = state.library.search.trim().toLowerCase();
      return state.library.items.filter((item) => {
        const matchesCategory = state.library.category === 'all' || item.category === state.library.category;
        const matchesSubcategory =
          state.library.subcategory === 'all' ||
          (item.subcategory || '') === state.library.subcategory;
        const matchesQuery =
          !query ||
          item.name.toLowerCase().includes(query) ||
          item.category.toLowerCase().includes(query) ||
          (item.subcategory || '').toLowerCase().includes(query);
        return matchesCategory && matchesSubcategory && matchesQuery;
      });
    };

    const renderLibrary = () => {
      if (!libraryGrid) return;
      const items = filteredLibraryItems();
      if (!items.length) {
        libraryGrid.innerHTML = '<p class="lbec-live-customizer__empty-note">No designs match this filter yet.</p>';
        return;
      }

      libraryGrid.innerHTML = '';
      items.forEach((item) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'lbec-live-customizer__design-item';
        const metaLabel = item.subcategory ? `${item.category} / ${item.subcategory}` : item.category;
        button.innerHTML = `
          <img src="${item.previewSrc}" alt="${item.name}">
          <span>${item.name}</span>
          <small>${metaLabel}</small>
        `;
        button.addEventListener('click', () => {
          addArtwork({
            name: item.name,
            src: item.previewSrc,
            previewable: Boolean(item.previewSrc),
            category: item.category,
            subcategory: item.subcategory || '',
            source: 'design_library',
          });
          hideLibrary();
        });
        libraryGrid.appendChild(button);
      });
    };

    const updateHiddenInputs = () => {
      const includeCustomization = state.mode !== 'blank';
      const payload = {
        product: productTitle,
        productUrl,
        variantId: variantHidden?.value || '',
        quantity: Number(quantityInput?.value || 1),
        orderType: state.mode === 'blank' ? 'Blanks only - no engraving' : 'Custom engraved item',
        artworkSource: getWorkflowLabel(),
        text: includeCustomization && getTextItemsInOrder()[0]
          ? {
              content: getTextItemsInOrder()[0].content,
              font: getTextItemsInOrder()[0].font.label,
              size: getTextItemsInOrder()[0].scale,
              rotation: getTextItemsInOrder()[0].rotation,
              position: { x: Math.round(getTextItemsInOrder()[0].x), y: Math.round(getTextItemsInOrder()[0].y) },
              placement: getTextItemsInOrder()[0].placementLabel,
            }
          : null,
        texts: includeCustomization
          ? getTextItemsInOrder().map((item) => ({
              content: item.content,
              font: item.font.label,
              bold: item.bold,
              italic: item.italic,
              underline: item.underline,
              size: item.scale,
              rotation: item.rotation,
              position: { x: Math.round(item.x), y: Math.round(item.y) },
              placement: item.placementLabel,
              order: item.order,
            }))
          : [],
        artworks: includeCustomization
          ? getUploadedArtworkItems().map((item) => ({
              name: item.name,
              source: item.source,
              category: item.category || '',
              subcategory: item.subcategory || '',
              previewable: item.previewable,
              scale: item.scale,
              rotation: item.rotation,
              position: { x: Math.round(item.x), y: Math.round(item.y) },
              placement: item.placementLabel,
              order: item.order,
            }))
          : [],
      };

      if (dataInput) dataInput.value = JSON.stringify(payload);
      if (orderTypeInput) orderTypeInput.value = payload.orderType;
      if (workflowInput) workflowInput.value = payload.artworkSource;
      if (textSummaryInput) textSummaryInput.value = includeCustomization ? getTextItemsInOrder().map((item) => item.content.trim()).filter(Boolean).join(' | ') : '';
      if (artworkSummaryInput) artworkSummaryInput.value = includeCustomization ? getUploadedArtworkItems().map((item) => item.name).join(', ') : '';
      if (previewSnapshotInput) {
        previewSnapshotInput.value = includeCustomization ? buildPreviewSnapshot() : '';
      }
    };

    const hasCustomization = () => state.artworks.length > 0;

    const validateBeforeSubmit = () => {
      if (!getSelectedVariantAvailable()) {
        return 'This product option is currently unavailable. Choose another option before adding it to cart.';
      }
      if (state.pendingUploads > 0) {
        return 'Artwork is still being prepared. Wait a moment, then add it to cart.';
      }
      if (state.mode !== 'blank' && !hasCustomization()) {
        return 'Add artwork or choose a design before adding this customized item to cart.';
      }
      const uploadArtworks = state.artworks.filter((item) => item.kind !== 'text' && item.source === 'upload');
      if (state.mode !== 'blank' && uploadArtworks.some((item) => item.file) && artworkInput && !artworkInput.files.length) {
        return 'Artwork is shown in the preview, but the original upload is not attached. Upload the file again before adding it to cart.';
      }
      return '';
    };

    const buildPreviewSnapshot = () => {
      if (!stage || !zone) return '';
      try {
        const stageRect = stage.getBoundingClientRect();
        if (!stageRect.width || !stageRect.height) return '';

        const canvas = document.createElement('canvas');
        canvas.width = 520;
        canvas.height = 520;
        const ctx = canvas.getContext('2d');
        if (!ctx) return '';

        const scaleX = canvas.width / stageRect.width;
        const scaleY = canvas.height / stageRect.height;

        ctx.fillStyle = '#f8f4ec';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        if (stageImage && stageImage.complete && stageImage.naturalWidth) {
          const imageRect = stageImage.getBoundingClientRect();
          ctx.drawImage(
            stageImage,
            (imageRect.left - stageRect.left) * scaleX,
            (imageRect.top - stageRect.top) * scaleY,
            imageRect.width * scaleX,
            imageRect.height * scaleY
          );
        }

        getArtworkItemsInOrder().forEach((item) => {
          const layer = artworkCanvas?.querySelector(`[data-layer-id="${item.id}"]`);
          if (!layer || layer.hidden) return;
          const rect = layer.getBoundingClientRect();
          const centerX = (rect.left - stageRect.left + rect.width / 2) * scaleX;
          const centerY = (rect.top - stageRect.top + rect.height / 2) * scaleY;
          ctx.save();
          ctx.translate(centerX, centerY);
          ctx.rotate((item.rotation * Math.PI) / 180);
          if (item.kind === 'text') {
            const fontPx = parseFloat(window.getComputedStyle(layer).fontSize || `${item.scale}`) * scaleX;
            const lines = item.content.split(/\r?\n/);
            ctx.fillStyle = getComputedStyle(layer).color || '#17110b';
            ctx.font = `${item.italic ? 'italic ' : ''}${item.bold ? '700' : '400'} ${fontPx}px ${item.font.stack}`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            const lineHeight = fontPx * 1.08;
            const blockOffset = ((lines.length - 1) * lineHeight) / 2;
            lines.forEach((line, lineIndex) => {
              ctx.fillText(line, 0, lineIndex * lineHeight - blockOffset);
            });
          } else if (item.previewable && item.src) {
            const image = layer.querySelector('img');
            if (image && image.complete) {
              ctx.drawImage(
                image,
                (-rect.width / 2) * scaleX,
                (-rect.height / 2) * scaleY,
                rect.width * scaleX,
                rect.height * scaleY
              );
            }
          } else {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect((-rect.width / 2) * scaleX, (-rect.height / 2) * scaleY, rect.width * scaleX, rect.height * scaleY);
            ctx.strokeStyle = 'rgba(23, 17, 11, 0.24)';
            ctx.strokeRect((-rect.width / 2) * scaleX, (-rect.height / 2) * scaleY, rect.width * scaleX, rect.height * scaleY);
          }
          ctx.restore();
        });

        return canvas.toDataURL('image/jpeg', 0.82);
      } catch (error) {
        return '';
      }
    };

    const renderTextLayer = () => {
      if (!textLayer) return;
      textLayer.hidden = true;
      textLayer.classList.remove('is-active');
    };

    const renderArtworkLayers = () => {
      if (!artworkCanvas) return;
      artworkCanvas.innerHTML = '';

      getArtworkItemsInOrder().forEach((item, index) => {
        const layer = document.createElement('button');
        layer.type = 'button';
        layer.className = item.kind === 'text' ? 'lbec-live-customizer__art-layer lbec-live-customizer__art-layer--text' : 'lbec-live-customizer__art-layer';
        layer.style.left = `${item.x}%`;
        layer.style.top = `${item.y}%`;
        if (item.kind === 'text') {
          layer.style.width = 'max-content';
          layer.style.maxWidth = '92%';
          layer.style.fontFamily = item.font.stack;
          layer.style.fontWeight = item.bold ? '700' : '400';
          layer.style.fontStyle = item.italic ? 'italic' : 'normal';
          layer.style.fontSize = `${item.scale}px`;
          layer.style.textDecoration = item.underline ? 'underline' : 'none';
        } else {
          layer.style.width = `${item.scale}%`;
        }
        layer.style.transform = `translate(-50%, -50%) rotate(${item.rotation}deg)`;
        layer.style.zIndex = String(index + 1);
        layer.dataset.layerKind = item.kind === 'text' ? 'text' : 'artwork';
        layer.dataset.layerId = item.id;
        layer.dataset.draggable = 'true';

        if (state.activeLayer?.type === 'artwork' && state.activeLayer.id === item.id) {
          layer.classList.add('is-active');
        }

        if (item.kind === 'text') {
          const span = document.createElement('span');
          span.textContent = item.content;
          layer.appendChild(span);
        } else if (item.previewable && item.src) {
          const image = document.createElement('img');
          image.src = item.src;
          image.alt = item.name;
          layer.appendChild(image);
        } else {
          layer.classList.add('is-fallback');
          layer.textContent = item.name;
        }

        layer.addEventListener('click', () => setActiveLayer({ type: 'artwork', id: item.id }));
        artworkCanvas.appendChild(layer);
      });
    };

    const renderArtworkList = () => {
      if (!artworkList) return;
      artworkList.innerHTML = '';

      if (!state.artworks.length) {
        artworkList.innerHTML = '<p class="lbec-live-customizer__empty-note">No artwork added yet.</p>';
        return;
      }

      getArtworkItemsInOrder().forEach((item, index) => {
        const row = document.createElement('div');
        row.className = 'lbec-live-customizer__artwork-row';
        if (state.activeLayer?.type === 'artwork' && state.activeLayer.id === item.id) {
          row.classList.add('is-active');
        }

        const typeLabel = item.kind === 'text' ? 'Text' : item.source === 'design_library' ? 'Library design' : 'Upload';
        const metaText = item.subcategory ? `${typeLabel} / ${item.subcategory}` : typeLabel;
          row.innerHTML = `
            <div class="lbec-live-customizer__artwork-thumb" data-thumb></div>
            <div class="lbec-live-customizer__artwork-row-copy">
              <strong data-title></strong>
              <small>${metaText}</small>
            </div>
            <div class="lbec-live-customizer__artwork-row-actions">
              <button type="button" class="lbec-live-customizer__row-button" data-action="select">Select</button>
              <button type="button" class="lbec-live-customizer__row-icon lbec-live-customizer__row-icon--arrow" data-action="up" aria-label="Move artwork up">▲</button>
              <button type="button" class="lbec-live-customizer__row-icon lbec-live-customizer__row-icon--arrow" data-action="down" aria-label="Move artwork down">▼</button>
              <button type="button" class="lbec-live-customizer__row-icon is-danger lbec-live-customizer__row-icon--trash" data-action="delete" aria-label="Delete artwork">
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="M9 4.5h6l.6 1.5H20v1.8H4V6h4.4L9 4.5Z" fill="currentColor"></path>
                  <path d="M7.2 8.4h9.6l-.8 10.2a1.6 1.6 0 0 1-1.59 1.4H9.59A1.6 1.6 0 0 1 8 18.6L7.2 8.4Z" fill="currentColor"></path>
                </svg>
              </button>
            </div>
          `;

        const thumb = row.querySelector('[data-thumb]');
        const title = row.querySelector('[data-title]');
        if (title) {
          title.textContent = `${index + 1}. ${item.kind === 'text' ? item.content : item.name}`;
        }
        if (thumb) {
          if (item.kind === 'text') {
            thumb.textContent = 'T';
          } else if (item.previewable && item.src) {
            const img = document.createElement('img');
            img.src = item.src;
            img.alt = item.name;
            thumb.appendChild(img);
          } else {
            thumb.textContent = 'File';
          }
        }

        row.querySelector('[data-action="select"]')?.addEventListener('click', () => {
          setActiveLayer({ type: 'artwork', id: item.id });
        });
        const upButton = row.querySelector('[data-action="up"]');
        if (upButton) {
          upButton.textContent = '^';
          upButton.addEventListener('click', () => moveArtwork(item.id, -1));
        }
        const downButton = row.querySelector('[data-action="down"]');
        if (downButton) {
          downButton.textContent = 'v';
          downButton.addEventListener('click', () => moveArtwork(item.id, 1));
        }
        const deleteButton = row.querySelector('[data-action="delete"]');
        if (deleteButton) {
          deleteButton.innerHTML = TRASH_ICON;
          deleteButton.addEventListener('click', () => removeArtwork(item.id));
        }

        artworkList.appendChild(row);
      });
    };

    const syncArtworkControls = () => {
      const activeArtwork = getActiveArtwork();
      if (!artworkControls) return;
      artworkControls.hidden = !activeArtwork || activeArtwork.kind === 'text';
      if (!activeArtwork || activeArtwork.kind === 'text') return;
      if (selectedArtworkLabel) {
        selectedArtworkLabel.textContent = activeArtwork.name;
      }

      if (artScaleInput) artScaleInput.value = String(activeArtwork.scale);
      if (artRotationInput) artRotationInput.value = String(activeArtwork.rotation);
      if (artPlacementSelect) {
        const options = Array.from(artPlacementSelect.options);
        const matching = options.find((option) => option.value === activeArtwork.placementLabel);
        if (matching) artPlacementSelect.value = matching.value;
      }
    };

    const syncTextControls = () => {
      const activeText = getActiveTextArtwork();
      const displayText = activeText ? activeText.content : state.text.draft;
      if (textInput && document.activeElement !== textInput && textInput.value !== displayText) {
        textInput.value = displayText;
      }
      if (textSizeInput) textSizeInput.value = String(activeText?.scale || state.text.size);
      if (textRotationInput) textRotationInput.value = String(activeText?.rotation || state.text.rotation);
      if (fontSelect) {
        const fontLabel = activeText?.font?.label || state.text.font.label;
        if (Array.from(fontSelect.options).some((option) => option.value === fontLabel)) {
          fontSelect.value = fontLabel;
        }
      }
      textFormatButtons.forEach((button) => {
        const key = button.dataset.lbecTextFormat;
        const active = activeText ? Boolean(activeText[key]) : Boolean(state.text[key]);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    };

    const getLayerElement = (layer) => {
      if (!layer) return null;
      if (layer.type === 'artwork') {
        return artworkCanvas?.querySelector(`[data-layer-id="${layer.id}"]`) || null;
      }
      return null;
    };

    const getSelectedElement = () => getLayerElement(state.activeLayer);

    const constrainLayerWithinZone = (layer) => {
      if (!zone) return false;
      const element = getLayerElement(layer);
      if (!element || element.hidden) return false;

      const zoneRect = zone.getBoundingClientRect();
      let rect = element.getBoundingClientRect();
      let changed = false;

      if (rect.width > zoneRect.width || rect.height > zoneRect.height) {
        const widthRatio = zoneRect.width / Math.max(rect.width, 1);
        const heightRatio = zoneRect.height / Math.max(rect.height, 1);
        const fitRatio = Math.min(widthRatio, heightRatio, 1) * 0.92;

        if (fitRatio < 0.999) {
          const artwork = getArtworkById(layer.id);
          if (artwork) {
            const nextScale = clamp(Math.floor(artwork.scale * fitRatio), artwork.kind === 'text' ? 14 : 15, artwork.kind === 'text' ? 180 : 520);
            if (nextScale !== artwork.scale) {
              artwork.scale = nextScale;
              changed = true;
            }
          }
        }
      }

      if (changed) {
        return true;
      }

      rect = element.getBoundingClientRect();
      let deltaX = 0;
      let deltaY = 0;

      if (rect.left < zoneRect.left) deltaX += zoneRect.left - rect.left;
      if (rect.right > zoneRect.right) deltaX += zoneRect.right - rect.right;
      if (rect.top < zoneRect.top) deltaY += zoneRect.top - rect.top;
      if (rect.bottom > zoneRect.bottom) deltaY += zoneRect.bottom - rect.bottom;

      const xPercentDelta = zoneRect.width ? (deltaX / zoneRect.width) * 100 : 0;
      const yPercentDelta = zoneRect.height ? (deltaY / zoneRect.height) * 100 : 0;

      if (Math.abs(xPercentDelta) < 0.01 && Math.abs(yPercentDelta) < 0.01) {
        return false;
      }

      const artwork = getArtworkById(layer.id);
      if (!artwork) return false;
      artwork.x = clamp(artwork.x + xPercentDelta, 0, 100);
      artwork.y = clamp(artwork.y + yPercentDelta, 0, 100);

      return true;
    };

    const constrainAllLayers = () => {
      let changed = false;
      getArtworkItemsInOrder().forEach((item) => {
        changed = constrainLayerWithinZone({ type: 'artwork', id: item.id }) || changed;
      });
      return changed;
    };

    const syncSelectionBox = () => {
      if (!selectionBox || !zone) return;
      const selectedElement = getSelectedElement();
      if (!selectedElement || selectedElement.hidden) {
        selectionBox.hidden = true;
        return;
      }

      const zoneRect = zone.getBoundingClientRect();
      const rect = selectedElement.getBoundingClientRect();
      selectionBox.hidden = false;
      selectionBox.style.left = `${rect.left - zoneRect.left}px`;
      selectionBox.style.top = `${rect.top - zoneRect.top}px`;
      selectionBox.style.width = `${rect.width}px`;
      selectionBox.style.height = `${rect.height}px`;
    };

    const render = (pass = 0) => {
      if (library) {
        library.hidden = !state.library.open;
      }
      root.classList.toggle('is-blank-mode', state.mode === 'blank');
      customizationCards.forEach((card) => {
        card.hidden = state.mode === 'blank';
        card.querySelectorAll('input, textarea, select, button').forEach((input) => {
          input.disabled = state.mode === 'blank';
        });
      });
      if (artworkInput) {
        artworkInput.disabled = state.mode === 'blank';
      }
      modeButtons.forEach((button) => {
        const active = button.dataset.lbecModeButton === state.mode;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      workflowButtons.forEach((button) => {
        const active = button.dataset.lbecWorkflowButton === state.workflow;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      workflowPanels.forEach((panel) => {
        panel.hidden = panel.dataset.lbecWorkflowPanel !== state.workflow;
      });
      syncVariant();
      renderTextLayer();
      renderArtworkLayers();
      if (pass < 3 && constrainAllLayers()) {
        render(pass + 1);
        return;
      }
      renderArtworkList();
      syncUploadButtons();
      syncTextControls();
      syncArtworkControls();
      syncSelectionBox();
      updateHiddenInputs();
    };

    const addArtwork = (entry) => {
      if (state.artworks.length >= maxArtworks) return;
      switchToCustomizerImage();
      const placement = resolvePlacementMeta(artPlacementSelect);
      const nextOrder = state.artworks.length
        ? Math.max(...state.artworks.map((item) => item.order)) + 1
        : 1;
      const artwork = {
        id: uid(),
        kind: entry.kind || 'artwork',
        name: entry.name || `Artwork ${state.artworks.length + 1}`,
        src: entry.src || '',
        previewable: Boolean(entry.previewable),
        source: entry.source || 'upload',
        category: entry.category || '',
        file: entry.file || null,
        x: placement.x,
        y: placement.y,
        scale: ARTWORK_DEFAULT_SCALE,
        rotation: snapRotation(Number(artRotationInput?.value || 0)),
        placementLabel: placement.label,
        order: nextOrder,
      };

      if (artScaleInput) {
        artScaleInput.value = String(artwork.scale);
      }
      state.artworks.push(artwork);
      syncArtworkUploadInput();
      setActiveLayer({ type: 'artwork', id: artwork.id });
    };

    const removeArtwork = (artworkId) => {
      state.artworks = state.artworks.filter((item) => item.id !== artworkId);
      state.artworks
        .sort((a, b) => a.order - b.order)
        .forEach((item, index) => {
          item.order = index + 1;
        });
      syncArtworkUploadInput();
      if (state.activeLayer?.type === 'artwork' && state.activeLayer.id === artworkId) {
        const first = getArtworkItemsInOrder()[0];
        state.activeLayer = first ? { type: 'artwork', id: first.id } : null;
      }
      render();
    };

    const selectVariantOption = (option) => {
      if (!variantSelect || !option) return false;
      variantSelect.value = option.value;
      variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    };

    const findBlankVariantOption = () => {
      if (!variantSelect) return null;
      const current = variantSelect.selectedOptions?.[0];
      const currentTitle = (current?.dataset.variantTitle || current?.textContent || '').toLowerCase();
      const currentParts = currentTitle
        .split('/')
        .map((part) => part.trim())
        .filter(Boolean)
        .filter((part) => !part.includes('blank') && !part.includes('unengraved') && !part.includes('engraving'));
      const blankOptions = Array.from(variantSelect.options).filter((option) => {
        return option.dataset.blankVariant === 'true' && option.dataset.available !== 'false';
      });
      return blankOptions.find((option) => {
        const title = (option.dataset.variantTitle || option.textContent || '').toLowerCase();
        return currentParts.every((part) => title.includes(part));
      }) || blankOptions[0] || null;
    };

    const findCustomVariantOption = () => {
      if (!variantSelect) return null;
      return Array.from(variantSelect.options).find((option) => {
        return option.dataset.blankVariant !== 'true' && option.dataset.available !== 'false';
      }) || null;
    };

    const setMode = (mode) => {
      state.mode = mode === 'blank' ? 'blank' : 'custom';
      if (state.mode === 'blank') {
        const blankOption = findBlankVariantOption();
        if (!blankOption) {
          state.mode = 'custom';
          render();
          return;
        }
        selectVariantOption(blankOption);
        hideLibrary();
        clearActiveLayer();
      } else if (getSelectedVariantIsBlank()) {
        selectVariantOption(findCustomVariantOption());
      }
      render();
    };

    const moveArtwork = (artworkId, direction) => {
      const ordered = getArtworkItemsInOrder();
      const index = ordered.findIndex((item) => item.id === artworkId);
      if (index === -1) return;
      const swapIndex = index + direction;
      if (swapIndex < 0 || swapIndex >= ordered.length) return;
      const current = ordered[index];
      const swap = ordered[swapIndex];
      const currentOrder = current.order;
      current.order = swap.order;
      swap.order = currentOrder;
      render();
    };

    const setTextPosition = (x, y, label = 'Custom') => {
      const activeText = getActiveTextArtwork();
      if (!activeText) {
        setStatus('Select a text item before centering it.');
        return;
      }
      switchToCustomizerImage();
      clearStatus();
      activeText.x = clamp(x, 0, 100);
      activeText.y = clamp(y, 0, 100);
      activeText.placementLabel = label;
      setActiveLayer({ type: 'artwork', id: activeText.id });
    };

    const setArtworkPosition = (artwork, x, y, label = 'Custom') => {
      if (!artwork) {
        setStatus('Select an artwork file or design before using artwork placement controls.');
        return;
      }
      clearStatus();
      artwork.x = clamp(x, 0, 100);
      artwork.y = clamp(y, 0, 100);
      artwork.placementLabel = label;
      setActiveLayer({ type: 'artwork', id: artwork.id });
    };

    const handleTextAction = (action) => {
      const activeText = getActiveTextArtwork();
      if (!activeText) {
        setStatus('Add or select a text item before adjusting text size or rotation.');
        focusStatus();
        return;
      }
      switchToCustomizerImage();
      clearStatus();
      if (action === 'smaller') {
        activeText.scale = clamp(activeText.scale - 4, 14, 180);
      } else if (action === 'larger') {
        activeText.scale = clamp(activeText.scale + 4, 14, 180);
      } else if (action === 'rotate-left') {
        activeText.rotation = snapRotation(activeText.rotation - ROTATION_SNAP);
      } else if (action === 'rotate-right') {
        activeText.rotation = snapRotation(activeText.rotation + ROTATION_SNAP);
      }
      setActiveLayer({ type: 'artwork', id: activeText.id });
    };

    const addTextArtwork = () => {
      const content = (textInput?.value || state.text.draft || '').trim();
      if (!content) {
        setStatus('Type the text you want to add first.');
        focusStatus();
        return;
      }
      switchToCustomizerImage();
      clearStatus();
      const nextOrder = state.artworks.length
        ? Math.max(...state.artworks.map((item) => item.order)) + 1
        : 1;
      const artwork = {
        id: uid(),
        kind: 'text',
        name: `Text ${getTextItemsInOrder().length + 1}`,
        content,
        previewable: false,
        source: 'text',
        category: '',
        file: null,
        x: 50,
        y: 50,
        scale: Number(textSizeInput?.value || state.text.size || 32),
        rotation: snapRotation(Number(textRotationInput?.value || 0)),
        font: resolveFontMeta(fontSelect),
        bold: state.text.bold,
        italic: state.text.italic,
        underline: state.text.underline,
        placementLabel: 'Centered',
        order: nextOrder,
      };
      state.artworks.push(artwork);
      state.text.draft = '';
      if (textInput) textInput.value = '';
      setActiveLayer({ type: 'artwork', id: artwork.id });
    };

    const fitArtworkToArea = (artwork) => {
      if (!artwork) return;
      artwork.x = 50;
      artwork.y = 50;
      artwork.scale = 92;
      artwork.placementLabel = 'Fit area';
    };

    const handleArtworkAction = (action) => {
      const artwork = getActiveArtwork();
      if (!artwork) {
        setStatus('Select an artwork file or design before adjusting it.');
        focusStatus();
        return;
      }
      clearStatus();
      if (action === 'smaller') {
        artwork.scale = clamp(artwork.scale - 8, 15, 520);
      } else if (action === 'larger') {
        artwork.scale = clamp(artwork.scale + 8, 15, 520);
      } else if (action === 'rotate-left') {
        artwork.rotation = snapRotation(artwork.rotation - ROTATION_SNAP);
      } else if (action === 'rotate-right') {
        artwork.rotation = snapRotation(artwork.rotation + ROTATION_SNAP);
      } else if (action === 'center') {
        artwork.x = 50;
        artwork.y = 50;
        artwork.placementLabel = 'Centered';
      } else if (action === 'fit') {
        fitArtworkToArea(artwork);
      } else if (action === 'delete') {
        removeArtwork(artwork.id);
        return;
      } else if (action === 'nudge-up') {
        artwork.y = clamp(artwork.y - 4, 0, 100);
        artwork.placementLabel = 'Custom';
      } else if (action === 'nudge-down') {
        artwork.y = clamp(artwork.y + 4, 0, 100);
        artwork.placementLabel = 'Custom';
      } else if (action === 'nudge-left') {
        artwork.x = clamp(artwork.x - 4, 0, 100);
        artwork.placementLabel = 'Custom';
      } else if (action === 'nudge-right') {
        artwork.x = clamp(artwork.x + 4, 0, 100);
        artwork.placementLabel = 'Custom';
      }
      setActiveLayer({ type: 'artwork', id: artwork.id });
    };

    const handleAlignment = (button) => {
      const x = Number(button.dataset.x || 50);
      const y = Number(button.dataset.y || 50);
      const label = button.textContent.trim() || 'Custom';
      if (button.dataset.lbecAlignTarget === 'text') {
        setTextPosition(x, y, label);
      } else {
        setArtworkPosition(getActiveArtwork(), x, y, label);
      }
    };

    const applyPlacementToArtwork = () => {
      const activeArtwork = getActiveArtwork();
      if (!activeArtwork) return;
      const placement = resolvePlacementMeta(artPlacementSelect);
      activeArtwork.x = clamp(placement.x, 0, 100);
      activeArtwork.y = clamp(placement.y, 0, 100);
      activeArtwork.placementLabel = placement.label;
      render();
    };

    const beginMoveInteraction = (layer, event) => {
      if (!zone) return;
      const zoneRect = zone.getBoundingClientRect();
      if (layer.type === 'text') {
        state.interaction = {
          mode: 'move',
          layer,
          startClientX: event.clientX,
          startClientY: event.clientY,
          startX: state.text.x,
          startY: state.text.y,
          zoneWidth: zoneRect.width,
          zoneHeight: zoneRect.height,
        };
      } else {
        const artwork = getArtworkById(layer.id);
        if (!artwork) return;
        state.interaction = {
          mode: 'move',
          layer,
          startClientX: event.clientX,
          startClientY: event.clientY,
          startX: artwork.x,
          startY: artwork.y,
          zoneWidth: zoneRect.width,
          zoneHeight: zoneRect.height,
        };
      }
    };

    const beginResizeInteraction = (handle, event) => {
      if (!selectionBox || !zone || !state.activeLayer) return;
      const selectedElement = getSelectedElement();
      if (!selectedElement) return;

      const boxRect = selectionBox.getBoundingClientRect();
      const centerX = boxRect.left + boxRect.width / 2;
      const centerY = boxRect.top + boxRect.height / 2;

      if (state.activeLayer.type === 'text') {
        state.interaction = {
          mode: 'resize',
          handle,
          layer: state.activeLayer,
          centerX,
          centerY,
          startHalfWidth: boxRect.width / 2 || 1,
          startHalfHeight: boxRect.height / 2 || 1,
          startSize: state.text.size,
        };
      } else {
        const artwork = getActiveArtwork();
        if (!artwork) return;
        state.interaction = {
          mode: 'resize',
          handle,
          layer: state.activeLayer,
          centerX,
          centerY,
          startHalfWidth: boxRect.width / 2 || 1,
          startHalfHeight: boxRect.height / 2 || 1,
          startScale: artwork.scale,
        };
      }
    };

    const beginRotateInteraction = (event) => {
      if (!selectionBox || !state.activeLayer) return;
      const boxRect = selectionBox.getBoundingClientRect();
      const centerX = boxRect.left + boxRect.width / 2;
      const centerY = boxRect.top + boxRect.height / 2;
      const startAngle = Math.atan2(event.clientY - centerY, event.clientX - centerX) * (180 / Math.PI);
      const startRotation = state.activeLayer.type === 'text'
        ? state.text.rotation
        : getActiveArtwork()?.rotation || 0;

      state.interaction = {
        mode: 'rotate',
        layer: state.activeLayer,
        centerX,
        centerY,
        startAngle,
        startRotation,
      };
    };

    const updateMoveInteraction = (interaction, event) => {
      const deltaXPercent = ((event.clientX - interaction.startClientX) / interaction.zoneWidth) * 100;
      const deltaYPercent = ((event.clientY - interaction.startClientY) / interaction.zoneHeight) * 100;

      if (interaction.layer.type === 'text') {
        state.text.x = clamp(interaction.startX + deltaXPercent, 0, 100);
        state.text.y = clamp(interaction.startY + deltaYPercent, 0, 100);
      } else {
        const artwork = getArtworkById(interaction.layer.id);
        if (!artwork) return;
        artwork.x = clamp(interaction.startX + deltaXPercent, 0, 100);
        artwork.y = clamp(interaction.startY + deltaYPercent, 0, 100);
      }
    };

    const updateResizeInteraction = (interaction, event) => {
      const distanceX = Math.abs(event.clientX - interaction.centerX);
      const distanceY = Math.abs(event.clientY - interaction.centerY);
      let scaleRatio = 1;

      if (interaction.handle === 'e' || interaction.handle === 'w') {
        scaleRatio = distanceX / interaction.startHalfWidth;
      } else if (interaction.handle === 'n' || interaction.handle === 's') {
        scaleRatio = distanceY / interaction.startHalfHeight;
      } else {
        scaleRatio = Math.max(distanceX / interaction.startHalfWidth, distanceY / interaction.startHalfHeight);
      }

      scaleRatio = clamp(scaleRatio, 0.2, 6);

      if (interaction.layer.type === 'text') {
        state.text.size = clamp(Math.round(interaction.startSize * scaleRatio), 14, 180);
      } else {
        const artwork = getArtworkById(interaction.layer.id);
        if (!artwork) return;
        artwork.scale = clamp(Math.round(interaction.startScale * scaleRatio), 15, 520);
      }
    };

    const updateRotateInteraction = (interaction, event) => {
      const angle = Math.atan2(event.clientY - interaction.centerY, event.clientX - interaction.centerX) * (180 / Math.PI);
      const rotation = snapRotation(interaction.startRotation + (angle - interaction.startAngle));
      if (interaction.layer.type === 'text') {
        state.text.rotation = rotation;
      } else {
        const artwork = getArtworkById(interaction.layer.id);
        if (!artwork) return;
        artwork.rotation = rotation;
      }
    };

    const handlePointerDown = (event) => {
      const handle = event.target.closest('[data-lbec-handle]');
      if (handle) {
        event.preventDefault();
        event.stopPropagation();
        if (handle.dataset.lbecHandle === 'rotate') {
          beginRotateInteraction(event);
        } else {
          beginResizeInteraction(handle.dataset.lbecHandle, event);
        }
        return;
      }

      if (event.target.closest('[data-lbec-selection-box]')) {
        event.preventDefault();
        if (state.activeLayer) {
          beginMoveInteraction(state.activeLayer, event);
        }
        return;
      }

      const target = event.target.closest('[data-draggable]');
      if (!target) return;

      event.preventDefault();
      if (target.dataset.layerKind === 'text') {
        if (target.dataset.layerId) {
          setActiveLayer({ type: 'artwork', id: target.dataset.layerId });
          beginMoveInteraction({ type: 'artwork', id: target.dataset.layerId }, event);
        }
      } else if (target.dataset.layerKind === 'artwork' && target.dataset.layerId) {
        setActiveLayer({ type: 'artwork', id: target.dataset.layerId });
        beginMoveInteraction({ type: 'artwork', id: target.dataset.layerId }, event);
      }
    };

    zone?.addEventListener('pointerdown', handlePointerDown);
    selectionBox?.addEventListener('pointerdown', handlePointerDown);
    stage?.addEventListener('pointerdown', (event) => {
      const hitLayer = event.target.closest('[data-draggable]');
      const hitSelection = event.target.closest('[data-lbec-selection-box]');
      if (!hitLayer && !hitSelection) {
        clearActiveLayer();
      }
    });

    window.addEventListener('pointermove', (event) => {
      if (!state.interaction) return;
      if (state.interaction.mode === 'move') {
        updateMoveInteraction(state.interaction, event);
      } else if (state.interaction.mode === 'resize') {
        updateResizeInteraction(state.interaction, event);
      } else if (state.interaction.mode === 'rotate') {
        updateRotateInteraction(state.interaction, event);
      }
      render();
    });

    window.addEventListener('pointerup', () => {
      if (!state.interaction) return;
      state.interaction = null;
      render();
    });

    galleryButtons.forEach((button) => {
      const imageSrc = button.dataset.imageSrc || '';
      const imageAlt = button.dataset.imageAlt || productTitle || 'Product preview';
      const showHoverImage = () => {
        if (!imageSrc) return;
        state.preview.hoverSrc = imageSrc;
        state.preview.hoverAlt = imageAlt;
        applyPreviewImage();
      };
      const clearHoverImage = () => {
        if (state.preview.hoverSrc !== imageSrc) return;
        state.preview.hoverSrc = '';
        state.preview.hoverAlt = '';
        applyPreviewImage();
      };

      button.addEventListener('mouseenter', showHoverImage);
      button.addEventListener('focus', showHoverImage);
      button.addEventListener('mouseleave', clearHoverImage);
      button.addEventListener('blur', clearHoverImage);
      button.addEventListener('click', () => {
        if (!imageSrc || state.preview.customizing) {
          clearHoverImage();
          return;
        }
        state.preview.activeSrc = imageSrc;
        state.preview.activeAlt = imageAlt;
        state.preview.hoverSrc = '';
        state.preview.hoverAlt = '';
        applyPreviewImage();
      });
    });

    uploadButtons.forEach((button) => {
      button.addEventListener('click', () => {
        setWorkflow('upload');
        closeMenus();
        hideLibrary();
        artworkInput?.click();
      });
    });

    libraryButtons.forEach((button) => {
      button.addEventListener('click', () => {
        setWorkflow('designs');
        if (!library?.hidden) {
          hideLibrary();
          return;
        }
        openLibrary();
      });
    });

    workflowButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const workflow = button.dataset.lbecWorkflowButton;
        setWorkflow(workflow);
        if (workflow === 'designs') {
          openLibrary();
        }
      });
    });

    artworkInput?.addEventListener('change', () => {
      const files = Array.from(artworkInput.files || []);
      const messages = [];
      const acceptedFiles = files.filter((file) => {
        if (!ALLOWED_UPLOAD_PATTERN.test(file.name)) {
          messages.push(`${file.name} is not a supported artwork file.`);
          return false;
        }
        if (file.size > MAX_UPLOAD_BYTES) {
          messages.push(`${file.name} is ${formatBytes(file.size)}. Uploads must be ${formatBytes(MAX_UPLOAD_BYTES)} or smaller.`);
          return false;
        }
        return true;
      });
      const remaining = Math.max(maxArtworks - state.artworks.length, 0);
      if (acceptedFiles.length > remaining) {
        messages.push(`Only ${remaining} more artwork ${remaining === 1 ? 'file' : 'files'} can be added to this item.`);
      }
      const filesToAdd = acceptedFiles.slice(0, remaining);
      if (messages.length) {
        setStatus(messages.join(' '));
      } else {
        clearStatus();
      }
      filesToAdd.forEach((file) => {
        const previewable = PREVIEWABLE_PATTERN.test(file.name);
        if (!previewable) {
          addArtwork({
            name: file.name,
            src: '',
            previewable: false,
            source: 'upload',
            file,
          });
          return;
        }

        const reader = new FileReader();
        state.pendingUploads += 1;
        syncSubmitAvailability();
        reader.onload = () => {
          state.pendingUploads = Math.max(0, state.pendingUploads - 1);
          addArtwork({
            name: file.name,
            src: String(reader.result || ''),
            previewable: true,
            source: 'upload',
            file,
          });
        };
        reader.onerror = () => {
          state.pendingUploads = Math.max(0, state.pendingUploads - 1);
          setStatus(`${file.name} could not be read for preview. Try uploading it again, or send it through the quote form.`);
          syncSubmitAvailability();
          updateHiddenInputs();
        };
        reader.readAsDataURL(file);
      });
    });

    textInput?.addEventListener('input', () => {
      const activeText = getActiveTextArtwork();
      if (activeText) {
        activeText.content = textInput.value;
        switchToCustomizerImage();
        clearStatus();
      } else {
        state.text.draft = textInput.value;
      }
      render();
    });

    textAddButton?.addEventListener('click', addTextArtwork);

    textClearButton?.addEventListener('click', () => {
      const activeText = getActiveTextArtwork();
      if (activeText) {
        removeArtwork(activeText.id);
        if (textInput) textInput.value = '';
        state.text.draft = '';
        return;
      }
      state.text.draft = '';
      if (textInput) textInput.value = '';
      clearStatus();
      render();
    });

    textActionButtons.forEach((button) => {
      button.addEventListener('click', () => {
        handleTextAction(button.dataset.lbecTextAction || '');
      });
    });

    artworkActionButtons.forEach((button) => {
      button.addEventListener('click', () => {
        handleArtworkAction(button.dataset.lbecArtworkAction || '');
      });
    });

    alignButtons.forEach((button) => {
      button.addEventListener('click', () => handleAlignment(button));
    });

    fontSelect?.addEventListener('change', () => {
      const font = resolveFontMeta(fontSelect);
      const activeText = getActiveTextArtwork();
      if (activeText) {
        activeText.font = font;
      } else {
        state.text.font = font;
      }
      render();
    });

    textFormatButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const key = button.dataset.lbecTextFormat;
        const activeText = getActiveTextArtwork();
        if (activeText) {
          activeText[key] = !activeText[key];
        } else if (key) {
          state.text[key] = !state.text[key];
        }
        render();
      });
    });

    textSizeInput?.addEventListener('input', () => {
      const activeText = getActiveTextArtwork();
      if (activeText) {
        activeText.scale = Number(textSizeInput.value || activeText.scale || 32);
      } else {
        state.text.size = Number(textSizeInput.value || 32);
      }
      render();
    });

    textRotationInput?.addEventListener('input', () => {
      const value = snapRotation(Number(textRotationInput.value || 0));
      const activeText = getActiveTextArtwork();
      if (activeText) {
        activeText.rotation = value;
      } else {
        state.text.rotation = value;
      }
      textRotationInput.value = String(value);
      render();
    });

    artPlacementSelect?.addEventListener('change', applyPlacementToArtwork);

    artScaleInput?.addEventListener('input', () => {
      const activeArtwork = getActiveArtwork();
      if (!activeArtwork) return;
      activeArtwork.scale = Number(artScaleInput.value || 130);
      render();
    });

    artRotationInput?.addEventListener('input', () => {
      const activeArtwork = getActiveArtwork();
      if (!activeArtwork) return;
      const value = snapRotation(Number(artRotationInput.value || 0));
      activeArtwork.rotation = value;
      artRotationInput.value = String(value);
      render();
    });

    libraryCategory?.addEventListener('change', () => {
      state.library.category = libraryCategory.value;
      state.library.subcategory = 'all';
      populateLibrarySubcategories();
      renderLibrary();
    });

    librarySubcategory?.addEventListener('change', () => {
      state.library.subcategory = librarySubcategory.value;
      renderLibrary();
    });

    librarySearch?.addEventListener('input', () => {
      state.library.search = librarySearch.value;
      renderLibrary();
    });

    variantSelect?.addEventListener('change', syncVariant);

    modeButtons.forEach((button) => {
      button.addEventListener('click', () => {
        setMode(button.dataset.lbecModeButton);
      });
    });

    variantButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (!variantSelect || button.disabled) return;
        variantSelect.value = button.dataset.lbecVariantButton || '';
        variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    qtyDown?.addEventListener('click', () => {
      if (!quantityInput) return;
      quantityInput.value = String(Math.max(1, Number(quantityInput.value || 1) - 1));
      syncDisplayedPrice();
      updateHiddenInputs();
    });

    qtyUp?.addEventListener('click', () => {
      if (!quantityInput) return;
      quantityInput.value = String(Math.max(1, Number(quantityInput.value || 1) + 1));
      syncDisplayedPrice();
      updateHiddenInputs();
    });

    quantityInput?.addEventListener('input', () => {
      quantityInput.value = String(Math.max(1, Number(quantityInput.value || 1)));
      syncDisplayedPrice();
      updateHiddenInputs();
    });

    productForm?.addEventListener('submit', (event) => {
      syncArtworkUploadInput();
      updateHiddenInputs();
      const validationMessage = validateBeforeSubmit();
      if (validationMessage) {
        event.preventDefault();
        event.stopPropagation();
        setStatus(validationMessage);
        focusStatus();
        syncSubmitAvailability();
        return;
      }
      state.submitted = true;
      clearStatus();
    });

    backButton?.addEventListener('click', () => {
      let referrer = null;
      try {
        referrer = document.referrer ? new URL(document.referrer) : null;
      } catch (error) {
        referrer = null;
      }
      if (referrer && referrer.origin === window.location.origin && referrer.href !== window.location.href && window.history.length > 1) {
        window.history.back();
      } else {
        window.location.href = fallbackBackUrl;
      }
    });

    shareButton?.addEventListener('click', async () => {
      const shareUrl = productUrl ? `${window.location.origin}${productUrl}` : window.location.href;
      try {
        if (navigator.share) {
          await navigator.share({ title: productTitle, url: shareUrl });
        } else if (navigator.clipboard) {
          await navigator.clipboard.writeText(shareUrl);
        }
        if (shareStatus) {
          shareStatus.hidden = false;
          window.setTimeout(() => {
            shareStatus.hidden = true;
          }, 1800);
        }
      } catch (error) {
        /* no-op */
      }
    });

    document.addEventListener('pointerdown', (event) => {
      if (!root.contains(event.target)) return;
      const keepLibraryOpen =
        event.target.closest('[data-lbec-open-library-inline]') ||
        event.target.closest('[data-lbec-library]');
      if (!keepLibraryOpen) {
        hideLibrary();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (!state.activeLayer) return;
      const target = event.target;
      const activeElement = document.activeElement;
      if (!root.contains(target) && !root.contains(activeElement)) return;
      const tagName = target?.tagName?.toLowerCase?.() || '';
      const isEditable =
        target?.isContentEditable ||
        tagName === 'input' ||
        tagName === 'textarea' ||
        tagName === 'select';
      if (isEditable) return;
      if (event.key !== 'Delete' && event.key !== 'Backspace') return;

      event.preventDefault();
      if (state.activeLayer.type === 'artwork' && state.activeLayer.id) {
        removeArtwork(state.activeLayer.id);
      }
    });

    window.addEventListener('beforeunload', (event) => {
      if (state.submitted || state.mode === 'blank' || !hasCustomization()) return;
      event.preventDefault();
      event.returnValue = '';
    });

    syncVariant();
    render();
  };

  const initAll = (scope) => {
    (scope || document)
      .querySelectorAll('[data-lbec-live-customizer]')
      .forEach(initCustomizer);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();

